/**
 * AssetDrawerContent Component
 *
 * Displays the content section for each asset drawer tab.
 * Pure presentation component - all state management handled by parent.
 *
 * Uses local state pattern (via useAssetDrawerTab) to prevent tab alternation
 * bug caused by React Router setSearchParams race conditions during rapid
 * arrow key navigation.
 */

import { ClockIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { Container } from '@/components/legacy-ui';
import { Skeleton } from '@/components/ui/skeleton';
import { AssetOverview } from '@/sections/asset/components/overview';
import { buildOverviewAttributes } from '@/sections/asset/components/overview/buildOverviewAttributes';
import {
  AssetAffiliation,
  AssetStatusHistoryTab,
} from '@/sections/asset/components/tabs';
import { TechnologyAttributeUnion } from '@/sections/asset/components/tabs';
import {
  AttributesTabContent,
  isCloudResource,
} from '@/sections/asset/components/tabs';
import { VulnerabilitiesTab } from '@/sections/asset/components/tabs';
import { useApplicationHistory } from '@/sections/asset/hooks/useApplicationHistory';
import { applicationRollupKey } from '@/sections/asset/utils';
import { NotesTab } from '@/sections/NotesTab';
import { Asset, Attribute, SeverityOpenCounts } from '@/types';
import { Regex } from '@/utils/regex.util';

export interface AssetDrawerContentProps {
  asset: Asset;
  assetKey: string;
  assetWithRisk: Asset & {
    riskSummary: Record<string, number>;
  };
  riskCounts: SeverityOpenCounts | undefined;
  isInitialLoading: boolean;
  attributes: Record<string, Attribute[]>;
  technologies: TechnologyAttributeUnion[];
  entityIdentifier: string;
  entityGroup: string;
  isAssetGroupADomain: boolean;
  /** Handler to open the drawer's comment modal (omit to hide Add Note button) */
  onOpenCommentModal?: () => void;
  /** Current tab value from local state */
  currentTab: string;
  /** Tab change handler from local state hook */
  onTabChange: (tab: string) => void;
}

/**
 * AssetDrawerContent routes to the appropriate tab content based on selected tab:
 * - Overview: Main asset information with relationships and vulnerabilities
 * - Vulnerabilities: Detailed vulnerability list
 * - Attributes: Asset attributes and technologies
 * - Notes: Asset notes and comments
 * - History: Asset affiliation and change history
 *
 * This is a pure presentation component with no internal state.
 */
export function AssetDrawerContent({
  asset,
  assetKey,
  assetWithRisk,
  riskCounts,
  isInitialLoading,
  attributes,
  technologies,
  entityIdentifier,
  entityGroup,
  isAssetGroupADomain,
  onOpenCommentModal,
  currentTab,
  onTabChange,
}: AssetDrawerContentProps) {
  const isAssetIdentifierAnIpAddress =
    Regex.IPV4.test(entityIdentifier || '') ||
    Regex.IPV6.test(entityIdentifier || '');

  const ptr = attributes.ptr || [];
  const registrant = attributes.registrant || [];

  const isAssetNotFound = useMemo(
    () => Object.keys(asset).length === 0 && !isInitialLoading,
    [asset, isInitialLoading]
  );

  // Notes rollup: when this drawer shows (or rolls up to) an Application,
  // merge the parent + every member's history into one thread for the Notes
  // tab (notes derive from history[] — see getNotes). The History tab always
  // shows the current asset's own history.
  const { history: mergedHistory } = useApplicationHistory(asset);
  const historyTabAsset =
    applicationRollupKey(asset) && mergedHistory.length > 0
      ? { ...asset, history: mergedHistory }
      : asset;

  return (
    <div className="min-h-0 w-full flex-1 overflow-y-auto">
      <Container
        className="flex h-full flex-col p-0"
        tabIndex={-1}
        id={currentTab}
      >
        {isInitialLoading ? (
          <Skeleton className="m-6 w-[calc(100%-48px)]" />
        ) : (
          <>
            {currentTab === '' &&
              (isAssetNotFound ? (
                <div className="flex items-center gap-4 p-6">
                  <ClockIcon className="text-chariot-stroke size-6" />
                  <span className="text-chariot-text-secondary">
                    Asset is no longer detected in your attack surface
                  </span>
                </div>
              ) : (
                <AssetOverview
                  attributesByName={buildOverviewAttributes({
                    asset,
                    ptr,
                    entityIdentifier,
                    isIpAddress: isAssetIdentifierAnIpAddress,
                  })}
                  asset={assetWithRisk}
                  domain={
                    isAssetGroupADomain && entityGroup
                      ? {
                          name: entityGroup,
                          registrantOrg: registrant?.[0]?.value || '',
                        }
                      : undefined
                  }
                  riskCounts={riskCounts}
                  isAssetIdentifierAnIpAddress={isAssetIdentifierAnIpAddress}
                  isAssetIdentifierACloudResource={isCloudResource(asset)}
                  onTabChange={onTabChange}
                />
              ))}
            {currentTab === 'vulnerabilities' && (
              <VulnerabilitiesTab assetKey={assetKey || ''} asset={asset} />
            )}
            {currentTab === 'attributes' && (
              <AttributesTabContent asset={asset} technologies={technologies} />
            )}
            {currentTab === 'notes' && (
              <NotesTab
                resource={historyTabAsset}
                resourceType="Asset"
                onOpenCommentModal={onOpenCommentModal}
              />
            )}
            {currentTab === 'history' && (
              <AssetStatusHistoryTab asset={asset} />
            )}
            {currentTab === 'affiliation' && <AssetAffiliation asset={asset} />}
          </>
        )}
      </Container>
    </div>
  );
}
